import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, Car, Truck, ArrowLeftRight, Wallet, AlertTriangle, CheckCheck } from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  vehicle_added: Car,
  vehicle_delivered: Truck,
  vehicle_transferred: ArrowLeftRight,
  payment_received: Wallet,
  finance_pending: AlertTriangle,
};

const ACCENTS = {
  vehicle_added: "text-blue-600 bg-blue-50",
  vehicle_delivered: "text-orange-600 bg-orange-50",
  vehicle_transferred: "text-slate-600 bg-slate-100",
  payment_received: "text-emerald-600 bg-emerald-50",
  finance_pending: "text-amber-600 bg-amber-50",
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const fetchNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  // Anchor the portal popover to the bell button — recalculates on open,
  // window resize, and scroll so it always sits directly under the bell,
  // never trapped behind another card's stacking context.
  const updatePosition = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const width = 320;
    let left = rect.left;
    if (left + width + 8 > window.innerWidth) left = window.innerWidth - width - 8;
    if (left < 8) left = 8;
    setPos({ top: rect.bottom + 8, left });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!open) return;
      const inBtn = btnRef.current && btnRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inBtn && !inPop) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    fetchNotifs();
  };

  const clickItem = async (n) => {
    if (!n.is_read) {
      await api.post(`/notifications/${n.id}/read`);
      fetchNotifs();
    }
  };

  return (
    <div ref={ref}>
      <button
        ref={btnRef}
        data-testid="notifications-bell"
        onClick={() => { setOpen(!open); if (!open) fetchNotifs(); }}
        className="relative p-2 rounded-md hover:bg-slate-800 text-slate-300 hover:text-white transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span data-testid="notifications-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: 320, zIndex: 9999 }}
          className="bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden"
          data-testid="notifications-popover">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="font-heading font-semibold text-slate-900">Notifications</div>
            {items.length > 0 && (
              <button data-testid="notifications-markall" onClick={markAll}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-400">No notifications yet</div>
            )}
            {items.map((n) => {
              const Icon = ICONS[n.kind] || Bell;
              const accent = ACCENTS[n.kind] || "text-slate-600 bg-slate-100";
              return (
                <button key={n.id} onClick={() => clickItem(n)} data-testid={`notification-${n.id}`}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 ${
                    !n.is_read ? "bg-blue-50/50" : ""
                  }`}>
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${accent}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-800 leading-snug">{n.message}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.is_read && <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
